// UI 日志复用：从 GameEvent 生成可读文本
import type { GameEvent } from "@/types/game";
import type { ResponseKind } from "@/types/cards";

// 花色单字 → 展示口径（黑色/红色/蓝色；无花色牌等其它值原样返回）
function suitLabel(suit: string): string {
  if (suit === "红" || suit === "黑" || suit === "蓝") return `${suit}色`;
  return suit;
}

// 判定结果文案
function judgmentResultText(kind: ResponseKind | undefined, success: boolean): string {
  if (!kind) return success ? "判定成功" : "判定失败";
  if (success) return `${kind}成功`;
  return `判定失败，效果继续`;
}

export function formatEvent(
  e: GameEvent,
  names: Map<string, string>
): string | null {
  switch (e.type) {
    case "turn-start":
      return `第${e.round}圈 · ${names.get(e.playerId) ?? e.playerId} 回合`;
    case "turn-skip":
      return `${names.get(e.playerId) ?? e.playerId} 被冻结`;
    case "play": {
      const tgt = Array.isArray(e.targetId)
        ? e.targetId.map((t) => names.get(t) ?? t).join("、")
        : e.targetId
          ? names.get(e.targetId) ?? e.targetId
          : null;
      return `${names.get(e.playerId)} 打出【${e.cardId}】${tgt ? ` → ${tgt}` : ""}`;
    }
    case "skill":
      return `技能【${e.skillName}】`;
    case "equip":
      return `装备到 ${e.slot}`;
    case "damage":
      return `${names.get(e.targetId)} 受到 ${e.amount} 点 ${e.damageType}`;
    case "heal":
      return e.mp
        ? `${names.get(e.targetId)} 恢复 ${e.amount} 点魔法`
        : `${names.get(e.targetId)} 恢复 ${e.amount} 点生命`;
    case "crit":
      return `${names.get(e.sourceId)} 触发暴击！${names.get(e.targetId)} 受到 ${e.multiplier}倍 伤害`;
    case "judgment":
      return `${names.get(e.playerId)} 【${e.armorName}】判定【${e.cardName}】→ ${suitLabel(e.result)} · ${judgmentResultText(e.responseKind, e.success)}`;
    case "dying":
      return `${names.get(e.playerId)} 濒死！`;
    case "rescue":
      return `${names.get(e.playerId)} 被救回`;
    case "death":
      return `${names.get(e.playerId)} 阵亡`;
    case "revive":
      return `${names.get(e.playerId)} 重生(${e.amount}HP)`;
    case "status-add":
      return `${names.get(e.playerId)} 获得【${e.statusName}】${e.duration}回合`;
    case "status-remove":
      return `${names.get(e.playerId)} 的【${e.statusName}】解除`;
    case "transfer":
      return `${e.reason}：${names.get(e.fromId) ?? e.fromId} → ${names.get(e.toId) ?? e.toId}(${e.cardId})`;
    case "response":
      return `${names.get(e.playerId)} 以【${e.kind}】响应`;
    case "draw":
      return null;
    case "discard":
      return `${names.get(e.playerId)} 弃${e.count}张`;
    case "turn-end":
      return null;
    case "reshuffle":
      return `摸牌堆重洗(${e.count}张)`;
    case "pickup-window":
      return `拾遗机会：${names.get(e.victimId) ?? e.victimId}【${e.reason}】弃${e.count}张`;
    case "game-over":
      return `游戏结束：${e.winners.join("、")} 队获胜`;
    default:
      return null;
  }
}
